/**
 * SortableMeasurePill — a draggable measure row with column picker + aggregation.
 *
 * Uses @dnd-kit/sortable for drag-and-drop reordering within the Y values list.
 */
import { useCallback, type CSSProperties } from "react";
import { Button } from "@fluentui/react-components";
import { ChevronDownRegular, DismissRegular, ReOrderDotsVerticalRegular } from "@fluentui/react-icons";
import { FieldPickerPopover } from "./fieldPicker";
import OptionPickerPopover from "./OptionPickerPopover";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { DatasetViewColumn } from "../../../api/types";
import type { MeasureField, AggregationType } from "../visualConfig";
import { getDefaultAggForColumn } from "../visualConfig";
import { AGGREGATIONS, colLabelByName } from "./constants";
import { useFieldMappingStyles } from "./styles";

interface SortableMeasurePillProps {
  id: string;
  measure: MeasureField;
  columns: DatasetViewColumn[];
  viewColumns: DatasetViewColumn[];
  onChange: (m: MeasureField) => void;
  onRemove: () => void;
  canRemove: boolean;
  showDragHandle: boolean;
  /** Hide aggregation dropdown (scatter raw mode). */
  hideAggregation?: boolean;
}

export default function SortableMeasurePill({
  id,
  measure,
  columns,
  viewColumns,
  onChange,
  onRemove,
  canRemove,
  showDragHandle,
  hideAggregation,
}: SortableMeasurePillProps) {
  const styles = useFieldMappingStyles();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  // Pick the right grid-template class.
  // When hideAggregation is true, drop the 100px aggregation column.
  const gridClass = hideAggregation
    ? showDragHandle && canRemove
      ? styles.measurePillNoAggDragRemove
      : showDragHandle
        ? styles.measurePillNoAggDrag
        : canRemove
          ? styles.measurePillNoAggRemove
          : undefined
    : showDragHandle && canRemove
      ? styles.measurePillWithDragAndRemove
      : showDragHandle
        ? styles.measurePillWithDrag
        : canRemove
          ? styles.measurePillWithRemove
          : undefined;

  const inlineStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  /** When column changes, auto-set aggregation based on semantic type */
  const handleColumnChange = useCallback(
    (colName: string) => {
      const col = viewColumns.find((c) => c.name === colName);
      const agg = col ? getDefaultAggForColumn(col) : measure.aggregation;
      onChange({ ...measure, column: colName, aggregation: agg });
    },
    [viewColumns, measure, onChange],
  );

  return (
    <div
      ref={setNodeRef}
      style={inlineStyle}
      className={`${styles.measurePill}${gridClass ? ` ${gridClass}` : ""}`}
    >
      {showDragHandle && (
        <span
          className={styles.measureDrag}
          title="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <ReOrderDotsVerticalRegular />
        </span>
      )}
      <FieldPickerPopover
        columns={columns}
        value={measure.column}
        onSelect={handleColumnChange}
      >
        <Button
          className={styles.measureField}
          size="small"
          appearance="subtle"
          iconPosition="after"
          icon={<ChevronDownRegular />}
        >
          {measure.column ? colLabelByName(measure.column, viewColumns) : "Select column…"}
        </Button>
      </FieldPickerPopover>
      {!hideAggregation && (
        <OptionPickerPopover
          className={styles.aggDropdown}
          options={AGGREGATIONS}
          value={measure.aggregation}
          onSelect={(v) =>
            onChange({ ...measure, aggregation: v as AggregationType })
          }
        />
      )}
      {canRemove && (
        <Button
          appearance="subtle"
          size="small"
          icon={<DismissRegular />}
          onClick={onRemove}
          title="Remove measure"
        />
      )}
    </div>
  );
}